import React from 'react'
import { Divider } from 'antd'
import IntlMessages from '../../util/IntlMessages'
import { IFormatMessage } from '@vacationtracker/shared/types/display-in-days-hours'

interface IWorKWeek {
  days: number[]
  firstDayOfWeek: number
  separator?: JSX.Element | string
  withAnd?: string
}

const DAY_NAME_KEYS = ['app.sunday', 'app.monday', 'app.tuesday', 'app.wednesday', 'app.thursday', 'app.friday', 'app.saturday']

/**
 * The week's days as i18n keys, in display order. Copies first: callers pass the location's own
 * workWeek array, and sorting it in place reorders their state.
 */
const orderedDayKeys = (days: number[], firstDayOfWeek: number): string[] => {
  const ordered = [...days].sort((a, b) => a - b)
  // A week that starts on Monday ends on Sunday rather than opening with it.
  if (ordered[0] === 0 && firstDayOfWeek === 1) {
    ordered.push(ordered.shift() as number)
  }

  return ordered.map((day) => DAY_NAME_KEYS[day])
}

/**
 * What comes before the day at `index`: nothing for the first, the conjunction before the last of
 * several, the separator otherwise. A lone day has nothing to be joined to, so it gets neither.
 */
const joiner = (index: number, count: number, separator: JSX.Element | string, withAnd: string) => {
  if (index === 0) return ''
  if (withAnd && index === count - 1) return ` ${withAnd.trim()} `

  return separator
}

export const workWeekAsText = ({ days, firstDayOfWeek, separator = ', ', withAnd = '' }: IWorKWeek, formatMessage: (d: IFormatMessage) => string): string => {
  const keys = orderedDayKeys(days, firstDayOfWeek)

  return keys.map((key, index) => `${joiner(index, keys.length, separator, withAnd)}${formatMessage({ id: key })}`).join('')
}

const WorkWeek = ({ days, firstDayOfWeek, separator = <Divider type="vertical" />, withAnd = '' }: IWorKWeek): React.ReactElement => {
  const keys = orderedDayKeys(days, firstDayOfWeek)

  return (
    <>
      {keys.map((key, index) => (
        <span key={key}>
          {joiner(index, keys.length, separator, withAnd)}
          <IntlMessages id={key} />
        </span>
      ))}
    </>
  )
}

export default WorkWeek
